import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  House,
  SignOut,
  ArrowLeft,
  Translate
} from '@phosphor-icons/react';

export default function Layout({ children, title, showBack = true }) {
  const { user, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Header */}
      <header className="h-16 flex items-center px-4 lg:px-6 sticky top-0 z-50 shadow-sm"
        style={{ background: 'var(--dm-bg-card)', borderBottom: `1px solid var(--dm-border)` }}>
        <div className="flex items-center gap-4 flex-1">
          {!isHome && showBack ? (
            <button
              onClick={() => navigate('/')}
              data-testid="back-home-btn"
              className="flex items-center gap-2 px-4 py-2 transition-all duration-150 group rounded-lg"
              style={{ background: 'var(--dm-bg-card-hover)', color: 'var(--dm-accent)' }}
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
              <span className="hidden sm:inline font-medium">{t('home')}</span>
            </button>
          ) : (
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-black flex items-center justify-center rounded-lg">
                <House size={24} weight="bold" style={{ color: 'var(--dm-accent)' }} />
              </div>
              <div className="hidden sm:block">
                <h1 className="font-bold text-lg tracking-tight text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>
                  Dukaan<span style={{ color: 'var(--dm-accent)' }}>Mate</span>
                </h1>
              </div>
            </Link>
          )}

          {title && !isHome && (
            <h2 className="text-xl font-bold text-[var(--text)] ml-4 hidden md:block" style={{ fontFamily: 'Outfit' }}>
              {title}
            </h2>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            data-testid="language-toggle-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 transition-all rounded-lg text-sm font-medium"
            style={{ background: 'var(--dm-bg-card-hover)', color: 'var(--dm-text-muted)' }}
          >
            <Translate size={16} />
            <span>{language === 'en' ? 'Language - Hindi' : 'Language - English'}</span>
          </button>

          <span className="text-xs font-mono hidden sm:block" style={{ color: 'var(--dm-text-muted)' }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </span>
          
          <div className="flex items-center gap-3 pl-3" style={{ borderLeft: `1px solid var(--dm-border)` }}>
            <div className="w-9 h-9 bg-black flex items-center justify-center font-bold uppercase text-sm rounded-full"
              style={{ color: 'var(--dm-accent)' }}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium text-[var(--text)]">{user?.name}</p>
              <p className="text-xs capitalize" style={{ color: 'var(--dm-text-muted)' }}>{user?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 hover:text-[#EF4444] transition-colors"
              title={t('signOut')}
              style={{ color: 'var(--dm-text-muted)' }}
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 lg:p-6 overflow-auto">
        {children}
      </main>
    </div>
  );
}
